import type { AgentHarness, HarnessToolDefinition } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import type { AgentRunnerConfig } from "../types.js";
import type { AgentMode, TaskContext } from "@project/shared";
import { buildCommonTools, buildForceUpdateTaskStatusTool } from "./common-tools.js";
import { buildPmTools, buildUpdateTaskTool } from "./pm-tools.js";
import { buildDiscoveryTools } from "./discovery-tools.js";
import { buildCodeReviewTools } from "./code-review-tools.js";

// Re-export helpers
export { textResult, imageBlock } from "./helpers.js";

// ── Mode-based tool selection ─────────────────────────────────────────

function getTaskModeTools(agentMode: AgentMode | undefined, connection: AgentConnection) {
  if (agentMode === "discovery" || agentMode === "auto") {
    return [buildUpdateTaskTool(connection)];
  }
  return [];
}

function getModeTools(
  agentMode: AgentMode | undefined,
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
) {
  if (config.mode === "pack") {
    // Pack runners orchestrate children regardless of the agentMode axis:
    // subtask CRUD + start_child_cloud_build/stop_child_build/
    // approve_and_merge_pr are their whole job (they never build code).
    return buildPmTools(connection, { includePackTools: true });
  }
  if (config.mode === "task") return getTaskModeTools(agentMode, connection);

  switch (agentMode) {
    case "building":
      return context?.isParentTask ? buildPmTools(connection, { includePackTools: true }) : [];
    case "review":
    case "auto":
    case "discovery":
    case "help":
      return buildPmTools(connection, {
        includePackTools: !!context?.isParentTask,
      });
    default:
      return config.mode === "pm" ? buildPmTools(connection, { includePackTools: false }) : [];
  }
}

// ── Tool assembly ─────────────────────────────────────────────────────

/** Collect all tools for the current mode as harness-neutral definitions. */
export function buildConveyorTools(
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
  agentMode?: AgentMode,
): HarnessToolDefinition[] {
  const effectiveMode = agentMode ?? context?.agentMode ?? undefined;

  const commonTools = buildCommonTools(connection, config);
  const modeTools = getModeTools(effectiveMode, connection, config, context);

  const discoveryTools =
    effectiveMode === "discovery" || effectiveMode === "auto"
      ? buildDiscoveryTools(connection)
      : [];

  // Code review tools available in review mode
  const codeReviewTools = effectiveMode === "review" ? buildCodeReviewTools(connection) : [];

  const emergencyTools = [buildForceUpdateTaskStatusTool(connection)];

  return [...commonTools, ...modeTools, ...discoveryTools, ...codeReviewTools, ...emergencyTools];
}

// ── MCP server factory ────────────────────────────────────────────────

// oxlint-disable-next-line typescript/explicit-function-return-type
export function createConveyorMcpServer(
  harness: AgentHarness,
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
  agentMode?: AgentMode,
) {
  return harness.createMcpServer({
    name: "conveyor",
    tools: buildConveyorTools(connection, config, context, agentMode),
  });
}
